import { useMemo } from 'react';
import type { CatalogSection } from '../api';

export type ValidationWarningSeverity = 'warning' | 'info';
export type ValidationWarningType =
  | 'no-variants'
  | 'unused-slot'
  | 'similar-ids'
  | 'many-variants'
  | 'invalid-slot-reference'
  | 'invalid-section-reference'
  | 'orphaned-section';
export type ValidationWarningAction = 'add-variant' | 'review-section' | 'fix-reference';

export interface ValidationWarning {
  id: string;
  severity: ValidationWarningSeverity;
  type: ValidationWarningType;
  message: string;
  sectionId?: string;
  entityType?: 'section' | 'template' | 'slot';
  entityId?: string;
  action?: ValidationWarningAction;
}

interface Template {
  label: string;
  slots: string[];
  recommended: string[];
}

interface UseWarningsOptions {
  sections: Array<{ id: string; data: CatalogSection }>;
  availableSlots: string[];
  templates?: Record<string, Template>; // NEW: Templates for cross-validation
  dismissedWarnings?: string[]; // Array of warning IDs dismissed by user
}

/**
 * Hook per generare warnings di validazione non bloccanti
 *
 * Features:
 * - Sezioni senza varianti (warning)
 * - Slot non utilizzati nei template (info)
 * - IDs molto simili - typo detection (warning)
 * - Sezioni con molte varianti >10 (info)
 * - Cross-entity validation:
 *   - Sections → Slots references (warning)
 *   - Templates → Slots references (warning)
 *   - Templates → Sections references (warning)
 *   - Orphaned sections detection (info)
 *
 * Performance: O(n²) worst case per similar IDs, ottimizzato con early exit
 */
export function useWarnings({ sections, availableSlots, templates = {}, dismissedWarnings = [] }: UseWarningsOptions) {
  const warnings = useMemo(() => {
    const allWarnings: ValidationWarning[] = [];

    // 1. Check: Sezioni senza varianti
    sections.forEach((section) => {
      const variants = section.data.variants || [];
      if (variants.length === 0) {
        allWarnings.push({
          id: `no-variants-${section.id}`,
          severity: 'warning',
          type: 'no-variants',
          message: `La sezione "${section.id}" non ha varianti definite`,
          sectionId: section.id,
          action: 'add-variant',
        });
      }
    });

    // 2. Check: Sezioni con molte varianti (>10)
    sections.forEach((section) => {
      const variants = section.data.variants || [];
      if (variants.length > 10) {
        allWarnings.push({
          id: `many-variants-${section.id}`,
          severity: 'info',
          type: 'many-variants',
          message: `La sezione "${section.id}" ha ${variants.length} varianti (potrebbe essere difficile da gestire)`,
          sectionId: section.id,
          action: 'review-section',
        });
      }
    });

    // 3. Check: IDs molto simili (Levenshtein distance ≤ 2)
    for (let i = 0; i < sections.length; i++) {
      for (let j = i + 1; j < sections.length; j++) {
        const id1 = sections[i].id;
        const id2 = sections[j].id;
        const distance = levenshteinDistance(id1, id2);

        // Warning se IDs sono molto simili (possibile typo)
        if (distance > 0 && distance <= 2) {
          allWarnings.push({
            id: `similar-ids-${id1}-${id2}`,
            severity: 'warning',
            type: 'similar-ids',
            message: `IDs simili rilevati: "${id1}" e "${id2}" (possibile typo)`,
            sectionId: id1,
            action: 'review-section',
          });
        }
      }
    }

    // 4. Check: Slot non utilizzati (info)
    // Trova tutti gli slot usati dalle sezioni
    const usedSlots = new Set<string>();
    sections.forEach((section) => {
      section.data.allowedSlots?.forEach(slot => usedSlots.add(slot));
    });

    // Trova slot disponibili ma mai usati
    const unusedSlots = availableSlots.filter(slot => !usedSlots.has(slot));
    if (unusedSlots.length > 0) {
      unusedSlots.forEach((slot) => {
        allWarnings.push({
          id: `unused-slot-${slot}`,
          severity: 'info',
          type: 'unused-slot',
          message: `Lo slot "${slot}" è disponibile ma non utilizzato da nessuna sezione`,
          entityType: 'slot',
          entityId: slot,
        });
      });
    }

    // === CROSS-ENTITY VALIDATIONS ===

    const availableSlotSet = new Set(availableSlots);
    const sectionIdSet = new Set(sections.map(s => s.id));

    // 5. Check: Sections → Slots references
    sections.forEach((section) => {
      const allowedSlots = section.data.allowedSlots || [];
      allowedSlots.forEach((slotId) => {
        if (!availableSlotSet.has(slotId)) {
          allWarnings.push({
            id: `invalid-slot-ref-section-${section.id}-${slotId}`,
            severity: 'warning',
            type: 'invalid-slot-reference',
            message: `Sezione "${section.id}" riferisce uno slot inesistente: "${slotId}"`,
            sectionId: section.id,
            entityType: 'section',
            entityId: section.id,
            action: 'fix-reference',
          });
        }
      });
    });

    // 6. Check: Templates → Slots references
    Object.entries(templates).forEach(([templateId, template]) => {
      const templateSlots = template.slots || [];
      templateSlots.forEach((slotId) => {
        if (!availableSlotSet.has(slotId)) {
          allWarnings.push({
            id: `invalid-slot-ref-template-${templateId}-${slotId}`,
            severity: 'warning',
            type: 'invalid-slot-reference',
            message: `Template "${templateId}" riferisce uno slot inesistente: "${slotId}"`,
            entityType: 'template',
            entityId: templateId,
            action: 'fix-reference',
          });
        }
      });
    });

    // 7. Check: Templates → Sections references (recommended)
    Object.entries(templates).forEach(([templateId, template]) => {
      const recommended = template.recommended || [];
      recommended.forEach((sectionId) => {
        if (!sectionIdSet.has(sectionId)) {
          allWarnings.push({
            id: `invalid-section-ref-template-${templateId}-${sectionId}`,
            severity: 'warning',
            type: 'invalid-section-reference',
            message: `Template "${templateId}" raccomanda una sezione inesistente: "${sectionId}"`,
            entityType: 'template',
            entityId: templateId,
            action: 'fix-reference',
          });
        }
      });
    });

    // 8. Check: Orphaned sections (sections not compatible with any template slots)
    sections.forEach((section) => {
      const allowedSlots = section.data.allowedSlots || [];

      // Check if any template uses any of the section's allowed slots
      const isUsedInTemplates = Object.values(templates).some(template => {
        const templateSlots = template.slots || [];
        return templateSlots.some(slotId => allowedSlots.includes(slotId));
      });

      if (!isUsedInTemplates && Object.keys(templates).length > 0) {
        allWarnings.push({
          id: `orphaned-section-${section.id}`,
          severity: 'info',
          type: 'orphaned-section',
          message: `Sezione "${section.id}" non è compatibile con nessuno slot dei template esistenti`,
          sectionId: section.id,
          entityType: 'section',
          entityId: section.id,
          action: 'review-section',
        });
      }
    });

    // Filtra warnings dismissati
    return allWarnings.filter(w => !dismissedWarnings.includes(w.id));
  }, [sections, availableSlots, templates, dismissedWarnings]);

  // Statistiche per badge
  const stats = useMemo(() => {
    const byType = warnings.reduce((acc, w) => {
      acc[w.type] = (acc[w.type] || 0) + 1;
      return acc;
    }, {} as Record<ValidationWarningType, number>);

    const bySeverity = warnings.reduce((acc, w) => {
      acc[w.severity] = (acc[w.severity] || 0) + 1;
      return acc;
    }, {} as Record<ValidationWarningSeverity, number>);

    return {
      total: warnings.length,
      byType,
      bySeverity,
    };
  }, [warnings]);

  return {
    warnings,
    stats,
    hasWarnings: warnings.length > 0,
  };
}

/**
 * Calcola la distanza di Levenshtein tra due stringhe
 * Utilizzato per rilevare IDs molto simili (typo detection)
 *
 * Complessità: O(m * n) dove m e n sono le lunghezze delle stringhe
 * Ottimizzazione: usa solo due righe della matrice invece di tutta la matrice
 */
function levenshteinDistance(str1: string, str2: string): number {
  const len1 = str1.length;
  const len2 = str2.length;

  // Ottimizzazione: se la differenza di lunghezza è >2, distanza sicuramente >2
  if (Math.abs(len1 - len2) > 2) return 3;

  // Array per dynamic programming (solo 2 righe invece di matrice completa)
  let prev = Array(len2 + 1).fill(0).map((_, i) => i);
  let curr = Array(len2 + 1).fill(0);

  for (let i = 1; i <= len1; i++) {
    curr[0] = i;

    for (let j = 1; j <= len2; j++) {
      if (str1[i - 1] === str2[j - 1]) {
        curr[j] = prev[j - 1];
      } else {
        curr[j] = 1 + Math.min(
          prev[j],      // deletion
          curr[j - 1],  // insertion
          prev[j - 1]   // substitution
        );
      }
    }

    // Ottimizzazione: early exit se la distanza minima in questa riga è >2
    if (Math.min(...curr) > 2) return 3;

    // Swap arrays
    [prev, curr] = [curr, prev];
  }

  return prev[len2];
}
